<template lang="pug">
  #WtJoinButton
    BTooltip.is-full-width(
      v-if="!showForfeitChallenge"
      :active="tooltip.active"
      :label="tooltip.label"
      position="is-left"
      animated
    )
      WtButton.wt-join-button(
        :class="[{ 'unset-background-color': isText }, (mapVariant && isJoinActive) ? 'wt-join-btn-map' : '']"
        semibold
        :is-router-link="false"
        :is-text="isText"
        :disabled="disabled"
        :loading="joiningOrRequesting"
        @click.native="joinOrRequest"
      )
        template(slot)
          span(:class="[joinedClass, (mapVariant && isJoinedOrRequestNotAcceptedYet) ? 'wt-join-btn-map-joined-text' : '']")
            | {{ joinedStatus }}
          BIcon(
            v-if="isJoinedOrRequestNotAcceptedYet"
            icon="check"
            :class="[joinedClass, (mapVariant && isJoinedOrRequestNotAcceptedYet) ? 'wt-join-btn-map-joined-icon' : '']"
          )
    template(v-if="showForfeitChallenge")
      ChallengeForfeitActionMenu(ref="forfeitActionMenu" :challenge="challenge")
        WtButton.wt-join-button(
          slot="trigger"
          :class="[{ 'unset-background-color': isText }, (mapVariant && isJoinActive) ? 'wt-join-btn-map' : '']"
          semibold
          :is-router-link="false"
          :is-text="isText"
          :loading="joiningOrRequesting"
          @click.native="handleForfeitButtonClick"
          :disabled="daysRemainingInChallenge(challenge) == 'Ended'"
        )
          template(slot)
            span(:class="[joinedClass, (mapVariant && isJoinedOrRequestNotAcceptedYet) ? 'wt-join-btn-map-joined-text' : '']")
              | {{ joinedStatus }}
            BIcon(
              v-if="isJoinedOrRequestNotAcceptedYet"
              icon="check"
              :class="[joinedClass, (mapVariant && isJoinedOrRequestNotAcceptedYet) ? 'wt-join-btn-map-joined-icon' : '']"
            )
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import ChallengeForfeitActionMenu from "@@/Challenge/ChallengeForfeitActionMenu";
import { challengeMixin } from "@/mixins/challengeMixin";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";

export default {
  name: "WtJoinButton",

  components: { WtButton, WtTitle, ChallengeForfeitActionMenu },

  mixins: [challengeMixin, dateUtilityMixin],

  props: {
    challenge: {
      type: Object,
      default: null
    },
    team: {
      type: Object,
      default: null
    },
    isText: {
      type: Boolean,
      default: true
    },
    teamJoinText: {
      type: String,
      default: "Add"
    },
    mapVariant: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      joiningOrRequesting: false,
      isModalActive: false
    };
  },

  computed: {
    ...mapGetters("members", ["memberTeams", "memberTeamInvites"]),
    ...mapGetters("program", ["programTeams", "coreProgramMeta"]),
    ...mapGetters([
      "members/challenges",
      "members/challengeInvites",
      "members/joinRequestMessages"
    ]),
    ...mapGetters("challenges", ["joining", "refreshJoinedStatusNeeded"]),
    ...mapGetters("teams", ["memberJoiningTeam"]),

    showForfeitChallenge() {
      // TODO the join will probably want to be refactored to include the same UI for, remove team from challengem and leave team
      return (
        !this.isTeamChallenge && this.isJoinChallengeButton && this.isJoined
      );
    },

    disabled() {
      if (this.isJoinChallengeButton) {
        return (
          !this.challenge.id ||
          this.isJoinedOrRequestNotAcceptedYet ||
          this.joining ||
          this.isNotActive ||
          (this.isTeamChallenge && !this.isTeamAdmin) ||
          // TODO there is still a bug where inactive challenges come up as: isActive=true
          // temporary fix is to rely on daysRemaining method to see if the challenge has ended
          this.daysRemainingInChallenge(this.challenge) == "Ended"
        );
      }
      if (this.isJoinTeamButton) {
        return (
          !this.team.id ||
          (this.isInviteOnly && !this.isInvited) ||
          this.isJoinedOrRequestNotAcceptedYet ||
          this.isNotActive ||
          this.memberJoiningTeam ||
          this.cannotJoinMultipleTeams ||
          this.teamIsFull ||
          this.organizationMismatch
        );
      }
      return true;
    },

    organizationMismatch() {
      if (!this.isJoinTeamButton) return false;
      const userOrgId = this.$auth.user().organizationId;
      const teamOrgId = this.team.organizationId;
      // Disable if team has organizationId but user's doesn't match
      return teamOrgId && userOrgId !== teamOrgId;
    },

    cannotJoinMultipleTeams() {
      return (
        !this.coreProgramMeta.memberCanJoinMultpleTeams &&
        this.memberTeams.length > 0
      );
    },

    teamIsFull() {
      if (!this.team) return false;
      return this.team && this.team.numMembers >= this.team.maxMembers;
    },

    tooltip() {
      const challengeTooltip =
        this.challenge &&
        this.isTeamChallenge &&
        !this.isTeamAdmin &&
        !this.isJoined;
      const teamToolTip =
        this.isJoinTeamButton && this.cannotJoinMultipleTeams && !this.isJoined;
      const organizationMismatchTooltip =
        this.isJoinTeamButton && this.organizationMismatch;

      if (challengeTooltip) {
        return {
          active: challengeTooltip,
          label: "Not An Admin."
        };
      } else if (teamToolTip) {
        return {
          active: teamToolTip,
          label: "Cannot Join Multiple Teams."
        };
      } else if (organizationMismatchTooltip) {
        return {
          active: organizationMismatchTooltip,
          label:
            "You cannot join this team because it belongs to a different organization."
        };
      }

      return {
        active: false,
        label: ""
      };
    },

    isJoinTeamButton() {
      return this.team && !this.challenge;
    },

    isJoinChallengeButton() {
      return this.challenge;
    },

    isTeamChallenge() {
      return this.challenge && this.challenge.between === "groups";
    },

    isJoined() {
      if (this.refreshJoinedStatusNeeded) {
        this["challenges/setRefreshStatusNeeded"](false);
        // refresh auth.user().challenges
        this.$auth.fetch();
      }
      if (this.isJoinChallengeButton) {
        if (this.challenge.between === "individuals") {
          // note, $auth.user() is refreshed after joining a challenge in the action 'challenges/join'
          // fallback to members store for an extra check and to keep sane while refacatoring
          const memberChallengeIds = this._.get(
            this.$auth.user(),
            "challenges",
            this["members/challenges"]
          ).map(item => item.id);

          return memberChallengeIds.includes(this.challenge.id);
        }

        if (this.isTeamChallenge) {
          const teamParticipating = this.challenge.teams.find(
            team => team.id === this.team.id
          );
          const isTeamParticipating = !!teamParticipating;
          const isMemberParticipating = this._.get(
            teamParticipating,
            "isMember",
            false
          );
          // JoinTeamChallenge listens and turns green if any of a members team is joined.
          this.$emit("isMemberParticipating", isMemberParticipating);
          return isTeamParticipating;
        }
      }
      if (this.isJoinTeamButton) {
        return this.team.isMember;
      }

      return false;
    },

    isInvited() {
      // TODO invite team to challenge
      if (this.isJoinChallengeButton) {
        const memberChallengeInviteIds = this["members/challengeInvites"].map(
          invite => invite.inviteId
        );
        return memberChallengeInviteIds.includes(this.challenge.id);
      }
      if (this.isJoinTeamButton) {
        const memberTeamInviteIds = this.memberTeamInvites.map(
          invite => invite.inviteId
        );

        return memberTeamInviteIds.includes(this.team.id) || this.team.isAdmin;
      }
      return false;
    },

    isJoinRequested() {
      // TODO team challenge
      if (this.isJoinChallengeButton) {
        const memberJoinRequestMessages = this[
          "members/joinRequestMessages"
        ].map(message => message.message);

        let value = 0;
        memberJoinRequestMessages.forEach(message => {
          value = value + message.includes(this.challenge.name);
        });

        return value >= 1;
      }
      if (this.isJoinTeamButton) {
        // TODO team
        return false;
      }
      return false;
    },

    isJoinedOrRequestNotAcceptedYet() {
      return this.isJoined || (this.isJoinRequested && !this.isInvited);
    },

    isInviteOnly() {
      if (this.isJoinChallengeButton) {
        return this.challenge.open === 0;
      }
      if (this.isJoinTeamButton) {
        return this.team.inviteOnly === 1;
      }
      return false;
    },

    isPublic() {
      if (this.isJoinChallengeButton) {
        return this.challenge.public === 1;
      }
      if (this.isJoinTeamButton) {
        return this.team.public === 1;
      }
      return false;
    },

    isValid() {
      return this.isPublic || (!this.isPublic && this.isInvited);
    },

    joinedStatus() {
      // TODO make this more readable please good lord
      const action = this.isTeamChallenge ? this.teamJoinText : "Join";
      if (this.isJoined) {
        return `${action}ed`;
      } else if (this.isJoinRequested && !this.isJoined && !this.isInvited) {
        return "Requested";
      } else if (this.isInviteOnly && this.isPublic && !this.isInvited) {
        return "Request invite";
      } else if (this.isNotActive) {
        if (this.isJoinChallengeButton) {
          return "Complete";
        }
        if (this.isJoinTeamButton) {
          return `Past ${action} Date`;
        }
        return "Is Not Active";
      } else if (this.teamIsFull && !this.isJoinChallengeButton) {
        return "Team Full";
      } else {
        return action;
      }
    },

    joinedClass() {
      if (this.teamIsFull) return "has-text-grey-dark";
      else return this.isJoined ? "has-text-special-success" : "";
    },

    isNotActive() {
      if (this.isJoinChallengeButton) {
        const COMPLETED = 2;
        return this.challenge.status === COMPLETED;
      }
      if (this.isJoinTeamButton) {
        return this.moment(this.team.deadlineDate).isBefore(this.moment());
      }
      return false;
    },

    isTeamAdmin() {
      return this.team.isAdmin;
    },

    isJoinActive() {
      // Button is in the 'Join' state if not joined, not requested, not disabled, and is a join challenge button
      return (
        this.isJoinChallengeButton &&
        !this.isJoined &&
        !this.isJoinRequested &&
        !this.disabled
      );
    }
  },

  methods: {
    ...mapActions("teams", ["addMembersToTeam"]),
    ...mapActions([
      "challenges/join",
      "challenges/request",
      "snackBar",
      "members/fetchRequestedJoinMessages",
      "challenges/setRefreshStatusNeeded"
    ]),

    joinOrRequest() {
      if (this.isJoinedOrRequestNotAcceptedYet) {
        return null;
      }

      if (!this.isInviteOnly || this.isInvited || this.$auth.user().isAdmin) {
        if (this.isJoinChallengeButton) {
          return this.joinChallenge();
        }
        if (this.isJoinTeamButton) {
          return this.joinTeam();
        }
      } else {
        if (this.isJoinChallengeButton) {
          return this.requestChallenge();
        }
        if (this.isJoinTeamButton) {
          return this.requestTeam();
        }
      }
    },

    async joinTeam() {
      let payload = {
        team: this.team,
        memberIds: [this.$auth.user().id]
      };

      this.joiningOrRequesting = true;

      await this.addMembersToTeam(payload);

      this.joiningOrRequesting = false;
    },

    async joinChallenge() {
      let payload = {
        challenge: this.challenge
      };

      if (this.challenge.between === "individuals") {
        payload.memberId = this.$auth.user().id;
      }
      if (this.isTeamChallenge) {
        payload.team = this.team;
      }

      this.joiningOrRequesting = true;

      await this["challenges/join"](payload);

      this.joiningOrRequesting = false;
    },

    async requestTeam() {
      // TODO team request
      return false;
    },

    async requestChallenge() {
      const payload = {
        challengeId: this.challenge.id,
        memberId: this.$auth.user().id
      };

      this.joiningOrRequesting = true;

      await this["challenges/request"](payload).then(async response => {
        if (response && response.data.success) {
          await this["members/fetchRequestedJoinMessages"](
            this.$auth.user().id
          );

          this.snackBar({
            message: `Request to join challenge ${this.challenge.name} successfully sent!`
          });
        }
      });

      this.joiningOrRequesting = false;
    },

    handleForfeitButtonClick() {
      // Call the openModal method on the ChallengeForfeitActionMenu component
      if (this.$refs.forfeitActionMenu) {
        this.$refs.forfeitActionMenu.openModal();
      }
    }
  }
};
</script>

<style scoped>
.is-full-width {
  width: 100%;
}

.wt-join-btn-map {
  background: #2972f0 !important;
  color: #fff !important;
  border-radius: 8px !important;
  width: 100% !important;
  height: 36px !important;
}

.wt-join-btn-map-joined-text {
  font-weight: 700 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  letter-spacing: 0px !important;
  color: #39b15a !important;
}

.wt-join-btn-map-joined-icon {
  font-weight: 700 !important;
  color: #39b15a !important;
}

.wt-join-button {
  border: none !important;
}

@media screen and (max-width: 768px) {
  /deep/ .wt-join-btn-map-joined-icon .mdi-24px.mdi:before {
    font-size: 20px !important;
    font-weight: 700 !important;
  }
}
</style>
